import { getOracleOffsetLabel } from 'src/utils/oracle';
import { formatText } from '@velocity-exchange/common/format';
import { PRESETS, withDecimals } from 'src/utils/formatNumber/presets';
import React from 'react';
import ScaledOrdersTable from 'src/components/TradeConfirmationModal/ScaledOrdersTable';
import ValueDisplay from 'src/components/ValueDisplay';
import TradeFormCollapsible from 'src/components/TradeForm/TradeFormCollapsible';
import SizeUnitToggleButton from 'src/components/TradeConfirmationModal/SizeUnitToggleButton';
import usePerpTradeConfirmationModal from './hooks/usePerpTradeConfirmationModal';
import { TRADE_CONFIRMATION_MODAL_DETAILS_CONTAINER_CLASSNAME } from 'src/components/TradeConfirmationModal/consts';
import { cm } from 'src/utils/classNames';
import TradeConfirmationModalWrapper from 'src/components/TradeConfirmationModal/TradeConfirmationModalWrapper';
import { TradeConfirmationModalProps } from 'src/components/TradeConfirmationModal/types';

export const PerpTradeConfirmationModal = ({
	tradeFormDetails,
}: TradeConfirmationModalProps) => {
	const {
		orderType,
		priceBoxValue,
		secondaryPriceBoxValue,
		tradeSide,
		stepSizeDecimalPlaces,
		tickSizeDecimalPlaces,
		actionValue,
		sizeDisplayLabel,
		sizeDisplayValue,
		actionValueClassName,
		estimatedLiquidationPriceUi,
		reduceOnly,
		postOnly,
		orderCount,
		scaledOrders,
		bracketOrders,
		isSubmitting,
		handleClose,
		handleConfirmation,
	} = usePerpTradeConfirmationModal({ tradeFormDetails });

	return (
		<TradeConfirmationModalWrapper
			tradeSide={tradeSide}
			isSubmitting={isSubmitting}
			handleConfirmation={handleConfirmation}
			handleClose={handleClose}
			reduceOnly={reduceOnly}
		>
			<div className={TRADE_CONFIRMATION_MODAL_DETAILS_CONTAINER_CLASSNAME}>
				<ValueDisplay.Default
					label="Action"
					value={actionValue}
					valueClassName={actionValueClassName}
				/>
				<ValueDisplay.Default
					label={sizeDisplayLabel}
					value={
						<div className="flex items-center gap-x-xs">
							{sizeDisplayValue}
							<SizeUnitToggleButton />
						</div>
					}
				/>
				{orderType === 'market' && (
					<ValueDisplay.Default label="Price" value="Market" />
				)}
				{['limit', 'stopLimit', 'stopMarket', 'takeProfitLimit'].includes(
					orderType
				) && (
					<ValueDisplay.Default
						label={orderType === 'limit' ? 'Limit price' : 'Trigger price'}
						value={formatText(
							priceBoxValue,
							withDecimals(PRESETS.usdTruncate, tickSizeDecimalPlaces)
						)}
					/>
				)}
				{(orderType === 'stopLimit' || orderType === 'takeProfitLimit') && (
					<ValueDisplay.Default
						label="Limit price"
						value={formatText(
							secondaryPriceBoxValue,
							withDecimals(PRESETS.usdTruncate, tickSizeDecimalPlaces)
						)}
					/>
				)}
				{orderType === 'oracleLimit' && (
					<ValueDisplay.Default
						label="Oracle offset"
						value={getOracleOffsetLabel(priceBoxValue, true)}
					/>
				)}
				{!reduceOnly && orderType !== 'scaledOrders' && (
					<ValueDisplay.Default
						label="Est. liquidation price"
						value={estimatedLiquidationPriceUi}
					/>
				)}
				{orderType === 'scaledOrders' && (
					<>
						<ValueDisplay.Default
							label="Start price"
							value={formatText(
								priceBoxValue,
								withDecimals(PRESETS.usdTruncate, tickSizeDecimalPlaces)
							)}
						/>
						<ValueDisplay.Default
							label="End price"
							value={formatText(
								secondaryPriceBoxValue,
								withDecimals(PRESETS.usdTruncate, tickSizeDecimalPlaces)
							)}
						/>
						<ValueDisplay.Default label="Order count" value={orderCount} />
					</>
				)}
				{(reduceOnly || postOnly) && (
					<ValueDisplay.Default
						label="Flag"
						value={reduceOnly ? 'Reduce only' : 'Post only'}
					/>
				)}
			</div>

			{(bracketOrders?.takeProfit?.price || bracketOrders?.stopLoss?.price) && (
				<div
					className={cm(
						TRADE_CONFIRMATION_MODAL_DETAILS_CONTAINER_CLASSNAME,
						'bg-transparent'
					)}
				>
					{bracketOrders.takeProfit?.price && (
						<ValueDisplay.Default
							label="Take profit"
							value={`Trigger price ${
								tradeSide === 'buy' ? '>' : '<'
							} ${formatText(bracketOrders.takeProfit.price.toNum(), withDecimals(PRESETS.usdTruncate, tickSizeDecimalPlaces))}`}
						/>
					)}
					{bracketOrders.stopLoss?.price && (
						<ValueDisplay.Default
							label="Stop loss"
							value={`Trigger price ${
								tradeSide === 'buy' ? '<' : '>'
							} ${formatText(bracketOrders.stopLoss.price.toNum(), withDecimals(PRESETS.usdTruncate, tickSizeDecimalPlaces))}`}
						/>
					)}
				</div>
			)}

			{orderType === 'scaledOrders' && scaledOrders && (
				<TradeFormCollapsible
					label="Orders"
					localStorageKey="tradeConfirmationScaledOrdersExpanded"
					contentClassName="mt-sm"
				>
					<ScaledOrdersTable
						orders={scaledOrders}
						tickSizeDecimalPlaces={tickSizeDecimalPlaces}
						stepSizeDecimalPlaces={stepSizeDecimalPlaces}
					/>
				</TradeFormCollapsible>
			)}
		</TradeConfirmationModalWrapper>
	);
};
